/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: inherit
 *
 * Contenido del sheet de filtros de Pagos (solo se usa dentro de
 * FilterSheetModal). Los pickers abren MasterFilterSheetModal anidados.
 */
import React, { useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import type { ExpenseSettlementMethod } from '../domain/expenseTypes';
import type { ExpensePaymentsHistoryFlow, ExpensePaymentsStatusFilter } from '../screens/hooks/useExpensePaymentsHistory';
import { EXPENSE_PERIOD_PRESETS } from '../application/expensesList.constants'
import type { ExpensePeriodPreset } from '../application/expensesList.constants';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal'
import { OperationalFilterFieldLabel, OperationalFilterPickerRow } from '../../shared/filters/OperationalFilterPrimitives'

const METHOD_FILTERS: Array<{ id: ExpenseSettlementMethod | 'all'; label: string }> = [
  { id: 'all', label: 'Todos' },
  { id: 'cash_drawer', label: 'Efectivo de la caja' },
  { id: 'transfer', label: 'Transferencia' },
  { id: 'card', label: 'Tarjeta' },
  { id: 'check', label: 'Cheque (cartera)' },
  { id: 'other', label: 'Otro' },
];

const STATUS_FILTERS: Array<{ id: ExpensePaymentsStatusFilter; label: string }> = [
  { id: 'active', label: 'Activos' },
  { id: 'all', label: 'Todos' },
  { id: 'voided', label: 'Anulados' },
];

type Props = {
  flow: ExpensePaymentsHistoryFlow;
};

export function ExpensePaymentsFiltersPanel({ flow }: Props) {
  const [periodSheetOpen, setPeriodSheetOpen] = useState(false);
  const [methodSheetOpen, setMethodSheetOpen] = useState(false);
  const [statusSheetOpen, setStatusSheetOpen] = useState(false);

  const periodPickerLabel = useMemo(
    () => EXPENSE_PERIOD_PRESETS.find((p) => p.id === flow.periodPreset)?.label ?? '30 días',
    [flow.periodPreset]
  );

  const methodPickerLabel = useMemo(
    () => METHOD_FILTERS.find((o) => o.id === flow.methodFilter)?.label ?? 'Todos',
    [flow.methodFilter]
  );

  const statusPickerLabel = useMemo(
    () => STATUS_FILTERS.find((o) => o.id === flow.statusFilter)?.label ?? 'Activos',
    [flow.statusFilter]
  );

  const periodSheetOptions = useMemo(
    () => EXPENSE_PERIOD_PRESETS.map((p) => ({ id: p.id, name: p.label })),
    []
  );

  const methodSheetOptions = useMemo(
    () => METHOD_FILTERS.map((o) => ({ id: o.id, name: o.label })),
    []
  );

  const statusSheetOptions = useMemo(
    () => STATUS_FILTERS.map((o) => ({ id: o.id, name: o.label })),
    []
  );

  const customDateRow =
    flow.periodPreset === 'custom' ? (
      <View style={styles.customDateRow}>
        <View style={styles.customDateField}>
          <InventoryIsoDateField
            value={flow.customFromDate}
            onChange={flow.setCustomFromDate}
            emptyLabel="Desde"
            ariaLabel="Fecha desde"
            showClearButton={false}
          />
        </View>
        <View style={styles.customDateField}>
          <InventoryIsoDateField
            value={flow.customToDate}
            onChange={flow.setCustomToDate}
            emptyLabel="Hasta"
            ariaLabel="Fecha hasta"
            showClearButton={false}
          />
        </View>
      </View>
    ) : null;

  return (
    <>
      <View style={styles.sheetBody}>
        <View style={styles.sheetSection}>
          <OperationalFilterFieldLabel>Período</OperationalFilterFieldLabel>
          <OperationalFilterPickerRow
            layout="stack"
            label="Período"
            valueLabel={periodPickerLabel}
            active={flow.periodPreset !== '30d'}
            onPress={() => setPeriodSheetOpen(true)}
          />
          {customDateRow}
        </View>
        <View style={styles.sheetSection}>
          <OperationalFilterFieldLabel>Detalle</OperationalFilterFieldLabel>
          <OperationalFilterPickerRow
            layout="stack"
            label="Método"
            valueLabel={methodPickerLabel}
            active={flow.methodFilter !== 'all'}
            onPress={() => setMethodSheetOpen(true)}
          />
          <OperationalFilterPickerRow
            layout="stack"
            label="Estado"
            valueLabel={statusPickerLabel}
            active={flow.statusFilter !== 'active'}
            onPress={() => setStatusSheetOpen(true)}
          />
        </View>
      </View>

      <MasterFilterSheetModal
        visible={periodSheetOpen}
        title="Período"
        options={periodSheetOptions}
        selectedId={flow.periodPreset}
        onSelect={(id) => {
          if (id) flow.setPeriodPreset(id as ExpensePeriodPreset);
        }}
        onClose={() => setPeriodSheetOpen(false)}
        showAllOption={false}
        appearance="ticket"
      />

      <MasterFilterSheetModal
        visible={methodSheetOpen}
        title="Método de pago"
        options={methodSheetOptions}
        selectedId={flow.methodFilter}
        onSelect={(id) => {
          if (id) flow.setMethodFilter(id as ExpenseSettlementMethod | 'all');
        }}
        onClose={() => setMethodSheetOpen(false)}
        showAllOption={false}
        appearance="ticket"
      />

      <MasterFilterSheetModal
        visible={statusSheetOpen}
        title="Estado"
        options={statusSheetOptions}
        selectedId={flow.statusFilter}
        onSelect={(id) => {
          if (id) flow.setStatusFilter(id as ExpensePaymentsStatusFilter);
        }}
        onClose={() => setStatusSheetOpen(false)}
        showAllOption={false}
        appearance="ticket"
      />
    </>
  );
}

const styles = StyleSheet.create({
  sheetBody: {
    gap: 18,
  },
  sheetSection: {
    gap: 8,
  },
  customDateRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 4,
    alignSelf: 'stretch',
  },
  customDateField: {
    flex: 1,
    minWidth: 0,
    maxWidth: 220,
  },
});
